import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const EnhancedEmailDashboard = () => {
  const { isDarkMode } = useAdminTheme();
  const [stats, setStats] = useState(null);
  const [stagingRecords, setStagingRecords] = useState([]);
  const [promotedRecords, setPromotedRecords] = useState([]);
  const [rejectedRecords, setRejectedRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [activeTab, setActiveTab] = useState('staging');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Enhanced loading states
  const [tabLoading, setTabLoading] = useState({});
  const [fetchProgress, setFetchProgress] = useState({ isFetching: false, progress: '', logs: [] });
  const [convertingRecord, setConvertingRecord] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [testingConnections, setTestingConnections] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load data sequentially to avoid overwhelming the server
      const statsRes = await api.get('/enhanced-email/stats');
      setStats(statsRes.data.stats);
      
      // Only load staging records if we're on the staging tab
      if (activeTab === 'staging') {
        const stagingRes = await api.get(`/enhanced-email/staging-records?limit=20&status=extracted&page=${currentPage}`);
        setStagingRecords(stagingRes.data.records);
        setTotalPages(stagingRes.data.pagination.pages);
      }
      
      // Only load promoted records if we're on the promoted tab
      if (activeTab === 'promoted') {
        const promotedRes = await api.get('/enhanced-email/promoted-records?limit=20&page=' + currentPage);
        setPromotedRecords(promotedRes.data.records);
        setTotalPages(promotedRes.data.pagination.pages);
      }
      
      // Only load rejected records if we're on the rejected tab
      if (activeTab === 'rejected') {
        const rejectedRes = await api.get('/enhanced-email/rejected-records?limit=20&page=' + currentPage);
        setRejectedRecords(rejectedRes.data.records);
        setTotalPages(rejectedRes.data.pagination.pages);
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage]);

  // Enhanced tab loading
  const loadTabData = useCallback(async (tab) => {
    try {
      setTabLoading(prev => ({ ...prev, [tab]: true }));
      
      switch (tab) {
        case 'staging':
          const stagingRes = await api.get(`/enhanced-email/staging-records?limit=20&status=extracted&page=${currentPage}`);
          setStagingRecords(stagingRes.data.records);
          setTotalPages(stagingRes.data.pagination.pages);
          break;
        case 'promoted':
          const promotedRes = await api.get('/enhanced-email/promoted-records?limit=20&page=' + currentPage);
          setPromotedRecords(promotedRes.data.records);
          setTotalPages(promotedRes.data.pagination.pages);
          break;
        case 'rejected':
          const rejectedRes = await api.get('/enhanced-email/rejected-records?limit=20&page=' + currentPage);
          setRejectedRecords(rejectedRes.data.records);
          setTotalPages(rejectedRes.data.pagination.pages);
          break;
        default:
          console.warn(`Unknown tab: ${tab}`);
          break;
      }
    } catch (error) {
      console.error(`Error loading ${tab} data:`, error);
      toast.error(`Failed to load ${tab} data`);
    } finally {
      setTabLoading(prev => ({ ...prev, [tab]: false }));
    }
  }, [currentPage]);

  // Enhanced stats refresh
  const refreshStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const statsRes = await api.get('/enhanced-email/stats');
      setStats(statsRes.data.stats);
    } catch (error) {
      console.error('Error refreshing stats:', error);
      toast.error('Failed to refresh stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Enhanced fetch emails with progress tracking and timeout handling
  const handleFetchEmails = async () => {
    setFetchProgress({
      isFetching: true,
      progress: 'Connecting to Gmail...',
      logs: ['🔄 Initializing email fetch process...']
    });
    
    try {
      // Add initial progress updates
      setFetchProgress(prev => ({
        ...prev,
        progress: 'Connecting to Gmail...',
        logs: [...prev.logs, '🔗 Connecting to Gmail server...']
      }));
      
      setFetchProgress(prev => ({
        ...prev,
        progress: 'Fetching emails...',
        logs: [...prev.logs, '📧 Fetching emails from unstructured_inquiries account...']
      }));
      
      // Add progress updates with timeouts
      setTimeout(() => {
        if (fetchProgress.isFetching) {
          setFetchProgress(prev => ({
            ...prev,
            progress: 'Processing emails (this may take a moment)...',
            logs: [...prev.logs, '⏳ Processing emails (this may take a moment)...']
          }));
        }
      }, 3000);
      
      setTimeout(() => {
        if (fetchProgress.isFetching) {
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, '🔄 Still processing... Please wait...']
          }));
        }
      }, 10000);
      
      // Start the fetch process with a timeout
      const fetchPromise = api.post('/enhanced-email/fetch', { 
        days: 7, 
        accountId: 'unstructured_inquiries' 
      }, { timeout: 120000 }); // 2 minute timeout
      
      const response = await fetchPromise;
      
      if (response.data.success) {
        const result = response.data;
        
        setFetchProgress(prev => ({
          ...prev,
          progress: 'Complete!',
          logs: [...prev.logs, '✅ Email processing completed successfully!']
        }));
        
        // Create detailed status message like structured emails
        const summary = result.summary || {};
        let statusMessage = `✅ Processed ${summary.emailsProcessed || 0} emails`;
        
        if (summary.emailsFetched > 0) {
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `📧 Fetched ${summary.emailsFetched} emails from server`]
          }));
        }
        
        if (summary.emailsStored > 0) {
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `💾 Stored ${summary.emailsStored} raw email records`]
          }));
        }
        
        if (summary.stagingRecordsCreated > 0) {
          statusMessage += ` • ${summary.stagingRecordsCreated} new staging records created`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `📝 Created ${summary.stagingRecordsCreated} new staging records`]
          }));
        }
        
        if (summary.aiSuccessRate > 0) {
          statusMessage += ` • ${summary.aiSuccessRate.toFixed(1)}% AI success rate`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `🤖 AI processing success rate: ${summary.aiSuccessRate.toFixed(1)}%`]
          }));
        }
        
        if (summary.fallbackRate > 0) {
          statusMessage += ` • ${summary.fallbackRate.toFixed(1)}% fallback parsing`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `🔄 Fallback parsing rate: ${summary.fallbackRate.toFixed(1)}%`]
          }));
        }
        
        if (summary.errors > 0) {
          statusMessage += ` • ${summary.errors} parsing issues`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `⚠️ ${summary.errors} emails had parsing issues`]
          }));
        }
        
        setFetchProgress(prev => ({
          ...prev,
          progress: statusMessage
        }));
        
        toast.success('Emails fetched and processed successfully!');
        
        // Automatically refresh dashboard on completion
        await loadDashboardData();
        
        // Clear progress message after 8 seconds (like structured emails)
        setTimeout(() => {
          setFetchProgress({ isFetching: false, progress: '', logs: [] });
        }, 8000);
        
      } else {
        setFetchProgress(prev => ({
          ...prev,
          progress: '❌ Email processing failed',
          logs: [...prev.logs, '❌ Email processing failed']
        }));
        throw new Error(response.data.message || 'Failed to fetch emails');
      }
      
    } catch (error) {
      console.error('Error fetching emails:', error);
      
      // Check if it's a timeout error
      if (error.message.includes('timeout') || error.code === 'ECONNABORTED') {
        setFetchProgress(prev => ({
          ...prev,
          progress: 'Processing in background...',
          logs: [...prev.logs, '⏳ Request timed out, but processing continues in background', '🔄 You will be notified when complete']
        }));
        
        toast.info('Email processing started - you will be notified when complete');
        
        // Clear progress message after 8 seconds
        setTimeout(() => {
          setFetchProgress({ isFetching: false, progress: '', logs: [] });
        }, 8000);
        
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch emails';
        setFetchProgress(prev => ({
          ...prev,
          progress: `❌ Error: ${errorMessage}`,
          logs: [...prev.logs, `❌ Error: ${errorMessage}`]
        }));
        
        toast.error('Failed to fetch emails: ' + errorMessage);
        
        // Clear error message after 8 seconds
        setTimeout(() => {
          setFetchProgress({ isFetching: false, progress: '', logs: [] });
        }, 8000);
      }
    }
  };

  const handleTestConnections = async () => {
    try {
      setTestingConnections(true);
      toast.info('Testing email connections...');
      
      // Create a timeout promise for 2 minutes
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection test timeout - please try again')), 120000); // 2 minute timeout
      });
      
      // Race between the API call and timeout
      const fetchPromise = api.post('/enhanced-email/test-connections');
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      console.log('Connection test results:', response.data);
      
      // Check if all connections are successful
      const results = response.data;
      const successfulConnections = [];
      const failedConnections = [];
      
      if (results.primary && results.primary.success) {
        successfulConnections.push('Primary Gmail');
      } else if (results.primary) {
        failedConnections.push('Primary Gmail');
      }
      
      if (results.customer_inquiries && results.customer_inquiries.success) {
        successfulConnections.push('Customer Inquiries Gmail');
      } else if (results.customer_inquiries) {
        failedConnections.push('Customer Inquiries Gmail');
      }
      
      if (results.unstructured_inquiries && results.unstructured_inquiries.success) {
        successfulConnections.push('Unstructured Inquiries Gmail');
      } else if (results.unstructured_inquiries) {
        failedConnections.push('Unstructured Inquiries Gmail');
      }
      
      // Show results
      if (successfulConnections.length > 0 && failedConnections.length === 0) {
        toast.success(`✅ All connections successful! (${successfulConnections.join(', ')})`);
      } else if (successfulConnections.length > 0 && failedConnections.length > 0) {
        toast.warning(`⚠️ Partial success: ${successfulConnections.join(', ')} connected, ${failedConnections.join(', ')} failed`);
      } else {
        toast.error(`❌ Connection failed for: ${failedConnections.join(', ')}`);
      }
      
    } catch (error) {
      console.error('Error testing connections:', error);
      
      // Check if it's a timeout error
      if (error.message.includes('timeout')) {
        toast.error('Connection test timed out after 2 minutes - please try again');
      } else {
        toast.error('Failed to test connections: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setTestingConnections(false);
    }
  };

  const handleReviewRecord = (record) => {
    setSelectedRecord(record);
    setShowReviewModal(true);
  };

  const handleRejectRecord = async (recordId, reason) => {
    try {
      const response = await api.post(`/enhanced-email/staging/${recordId}/reject`, {
        reason
      });
      console.log('Rejection result:', response.data);
      setShowReviewModal(false);
      await refreshStats();
      await loadTabData(activeTab);
      toast.success('Record rejected successfully');
    } catch (error) {
      console.error('Error rejecting record:', error);
      toast.error('Failed to reject record');
    }
  };

  const handleConvertToPayment = async (recordId, modifications = {}) => {
    try {
      setConvertingRecord(recordId);
      const response = await api.post(`/enhanced-email/staging/${recordId}/convert-to-payment`, {
        modifications
      });
      
      console.log('Conversion successful:', response.data);
      toast.success(`Successfully created payment request for ${response.data.paymentRequest.clientName}`);
      
      // Close modal immediately
      setShowReviewModal(false);
      setSelectedRecord(null);
      
      // Refresh data in background
      await refreshStats();
      await loadTabData(activeTab);
      
    } catch (error) {
      console.error('Error converting to payment request:', error);
      toast.error('Error creating payment request: ' + (error.response?.data?.message || error.message));
    } finally {
      setConvertingRecord(null);
    }
  };

  // Enhanced tab switching
  const handleTabChange = async (tab) => {
    if (tab === activeTab) return;
    
    setActiveTab(tab);
    setCurrentPage(1); // Reset page when switching tabs
    
    // Load data for the new tab
    await loadTabData(tab);
  };

  // Enhanced pagination
  const handlePageChange = async (page) => {
    if (page === currentPage) return;
    
    setCurrentPage(page);
    await loadTabData(activeTab);
  };

  const handleBulkConvert = async () => {
    if (selectedRecords.length === 0) {
      toast.error('Please select records to convert');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/enhanced-email/staging/bulk-convert-to-payment', {
        stagingIds: selectedRecords
      });
      
      console.log('Bulk conversion successful:', response.data);
      toast.success(`Successfully converted ${response.data.summary.successful} records to payment requests`);
      setSelectedRecords([]);
      await refreshStats();
      await loadDashboardData();
    } catch (error) {
      console.error('Error in bulk conversion:', error);
      toast.error('Error in bulk conversion: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedRecords.length === 0) {
      toast.error('Please select records to reject');
      return;
    }

    if (!bulkRejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/enhanced-email/staging/bulk-reject', {
        stagingIds: selectedRecords,
        reason: bulkRejectReason.trim()
      });
      
      console.log('Bulk rejection successful:', response.data);
      toast.success(`Successfully rejected ${response.data.summary.successful} staging records`);
      
      // Reset state
      setSelectedRecords([]);
      setBulkRejectReason('');
      setShowBulkRejectModal(false);
      
      // Refresh data
      await refreshStats();
      await loadDashboardData();
    } catch (error) {
      console.error('Error in bulk rejection:', error);
      toast.error('Error in bulk rejection: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async (type) => {

    const confirmMessage = `Are you sure you want to delete ${selectedRecords.length} ${type} records? This will permanently remove them from both email_raw and email_staging collections.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/enhanced-email/staging/bulk-delete', {
        stagingIds: selectedRecords,
        type: type
      });
      
      console.log('Bulk delete successful:', response.data);
      toast.success(`Successfully deleted ${response.data.deletedCount} ${type} records`);
      setSelectedRecords([]);
      await refreshStats();
      await loadTabData(activeTab);
    } catch (error) {
      console.error('Error in bulk delete:', error);
      toast.error('Failed to delete records: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };


  if (loading && !stats) {
    return (
      <div className={`flex items-center justify-center p-8 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
      }`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 transition-colors duration-300 ${
          isDarkMode ? 'border-blue-400' : 'border-blue-600'
        }`}></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Enhanced Email Processing</h1>
          <p className={`text-sm mt-1 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>AI-powered email processing and payment request creation</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleTestConnections}
            disabled={testingConnections}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
              testingConnections 
                ? (isDarkMode ? 'bg-blue-500 text-white cursor-not-allowed' : 'bg-blue-400 text-white cursor-not-allowed')
                : (isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700')
            }`}
          >
            {testingConnections ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Testing...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Test Connections</span>
              </>
            )}
          </button>
          <button
            onClick={handleFetchEmails}
            disabled={fetchProgress.isFetching}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
              isDarkMode 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {fetchProgress.isFetching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Fetch & Process Emails</span>
              </>
            )}
          </button>
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
              isDarkMode 
                ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark' 
                : 'bg-primary-700 text-white hover:bg-primary-600'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh Dashboard</span>
          </button>
        </div>
      </div>

      {/* Fetch Progress Log */}
      {(fetchProgress.isFetching || fetchProgress.progress) && (
        <div className={`border rounded-lg p-4 mb-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {fetchProgress.isFetching && (
                <div className={`animate-spin rounded-full h-5 w-5 border-b-2 transition-colors duration-300 ${
                  isDarkMode ? 'border-blue-400' : 'border-blue-600'
                }`}></div>
              )}
              <span className={`font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-blue-300' : 'text-blue-900'
              }`}>{fetchProgress.progress}</span>
            </div>
            {fetchProgress.progress.includes('background') && (
              <div className={`text-xs px-2 py-1 rounded transition-colors duration-300 ${
                isDarkMode ? 'text-blue-300 bg-blue-900/40' : 'text-blue-600 bg-blue-100'
              }`}>
                Background Processing
              </div>
            )}
          </div>
          
          {/* Real-time Logs */}
          {fetchProgress.logs.length > 0 && (
            <div className={`rounded border p-3 max-h-48 overflow-y-auto mb-3 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Processing Logs</h4>
                <button
                  onClick={() => setFetchProgress(prev => ({ ...prev, logs: [] }))}
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Clear
                </button>
              </div>
              <div className="space-y-1">
                {fetchProgress.logs.map((log, index) => (
                  <div key={index} className="flex items-start text-xs">
                    <span className={`mr-2 font-mono transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {new Date().toLocaleTimeString()}
                    </span>
                    <span className={`flex-1 transition-colors duration-300 ${
                      log.includes('✅') ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                      log.includes('❌') ? (isDarkMode ? 'text-red-400' : 'text-red-600') :
                      log.includes('⚠️') ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600') :
                      (isDarkMode ? 'text-gray-300' : 'text-gray-700')
                    }`}>
                      {log}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {fetchProgress.progress.includes('background') && (
            <div className={`text-xs transition-colors duration-300 ${
              isDarkMode ? 'text-blue-300' : 'text-blue-600'
            }`}>
              💡 Processing continues in the background. The dashboard will update automatically when complete.
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className={`p-6 rounded-lg shadow relative transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Raw Emails</h3>
              <p className={`text-2xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats?.rawEmails?.total || 0}</p>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>{stats?.rawEmails?.pending || 0} pending</p>
            </div>
            <div className={`transition-colors duration-300 ${
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            }`}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          {statsLoading && (
            <div className={`absolute inset-0 flex items-center justify-center transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark/80' : 'bg-white bg-opacity-75'
            }`}>
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 transition-colors duration-300 ${
                isDarkMode ? 'border-blue-400' : 'border-blue-600'
              }`}></div>
            </div>
          )}
        </div>
        
        <div className={`p-6 rounded-lg shadow relative transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Staging Records</h3>
              <p className={`text-2xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats?.stagingRecords?.total || 0}</p>
            </div>
            <div className={`transition-colors duration-300 ${
              isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
            }`}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
          {statsLoading && (
            <div className={`absolute inset-0 flex items-center justify-center transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark/80' : 'bg-white bg-opacity-75'
            }`}>
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 transition-colors duration-300 ${
                isDarkMode ? 'border-blue-400' : 'border-blue-600'
              }`}></div>
            </div>
          )}
        </div>
        
        <div className={`p-6 rounded-lg shadow relative transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Promoted</h3>
              <p className={`text-2xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats?.stagingRecords?.promoted || 0}</p>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>converted to payments</p>
            </div>
            <div className={`transition-colors duration-300 ${
              isDarkMode ? 'text-green-400' : 'text-green-600'
            }`}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          {statsLoading && (
            <div className={`absolute inset-0 flex items-center justify-center transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark/80' : 'bg-white bg-opacity-75'
            }`}>
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 transition-colors duration-300 ${
                isDarkMode ? 'border-blue-400' : 'border-blue-600'
              }`}></div>
            </div>
          )}
        </div>
        
        <div className={`p-6 rounded-lg shadow relative transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Rejected</h3>
              <p className={`text-2xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{stats?.stagingRecords?.rejected || 0}</p>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>declined by admin</p>
            </div>
            <div className={`transition-colors duration-300 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <button
            onClick={refreshStats}
            disabled={statsLoading}
            className={`absolute top-2 right-2 p-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="Refresh stats"
          >
            <svg className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {statsLoading && (
            <div className={`absolute inset-0 flex items-center justify-center transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark/80' : 'bg-white bg-opacity-75'
            }`}>
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 transition-colors duration-300 ${
                isDarkMode ? 'border-blue-400' : 'border-blue-600'
              }`}></div>
            </div>
          )}
        </div>
      </div>

      {/* Tabbed Interface */}
      <div className={`rounded-lg shadow transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
      }`}>
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange('staging')}
              disabled={tabLoading.staging}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'staging'
                  ? (isDarkMode ? 'border-blue-400 text-blue-400' : 'border-blue-500 text-blue-600')
                  : (isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300')
              } ${tabLoading.staging ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center space-x-2">
                <span>Staging Records</span>
                <span className={`text-xs px-2 py-1 rounded-full transition-colors duration-300 ${
                  isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {stats?.stagingRecords?.total || 0}
                </span>
                {tabLoading.staging && (
                  <div className={`animate-spin rounded-full h-3 w-3 border-b-2 transition-colors duration-300 ${
                    isDarkMode ? 'border-blue-400' : 'border-blue-600'
                  }`}></div>
                )}
              </div>
            </button>
            
            <button
              onClick={() => handleTabChange('promoted')}
              disabled={tabLoading.promoted}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'promoted'
                  ? (isDarkMode ? 'border-blue-400 text-blue-400' : 'border-blue-500 text-blue-600')
                  : (isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300')
              } ${tabLoading.promoted ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center space-x-2">
                <span>Promoted</span>
                <span className={`text-xs px-2 py-1 rounded-full transition-colors duration-300 ${
                  isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800'
                }`}>
                  {stats?.stagingRecords?.promoted || 0}
                </span>
                {tabLoading.promoted && (
                  <div className={`animate-spin rounded-full h-3 w-3 border-b-2 transition-colors duration-300 ${
                    isDarkMode ? 'border-blue-400' : 'border-blue-600'
                  }`}></div>
                )}
              </div>
            </button>
            
            <button
              onClick={() => handleTabChange('rejected')}
              disabled={tabLoading.rejected}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'rejected'
                  ? (isDarkMode ? 'border-blue-400 text-blue-400' : 'border-blue-500 text-blue-600')
                  : (isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300')
              } ${tabLoading.rejected ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center space-x-2">
                <span>Rejected</span>
                <span className={`text-xs px-2 py-1 rounded-full transition-colors duration-300 ${
                  isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800'
                }`}>
                  {stats?.stagingRecords?.rejected || 0}
                </span>
                {tabLoading.rejected && (
                  <div className={`animate-spin rounded-full h-3 w-3 border-b-2 transition-colors duration-300 ${
                    isDarkMode ? 'border-blue-400' : 'border-blue-600'
                  }`}></div>
                )}
              </div>
            </button>
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {/* Loading State */}
          {(loading || tabLoading[activeTab]) && (
            <div className="flex justify-center items-center py-8">
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 transition-colors duration-300 ${
                isDarkMode ? 'border-blue-400' : 'border-blue-600'
              }`}></div>
              <span className={`ml-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {tabLoading[activeTab] ? `Loading ${activeTab}...` : 'Loading...'}
              </span>
            </div>
          )}
          
          {/* Content */}
          {!loading && !tabLoading[activeTab] && (
            <>
              {/* Staging Records Tab */}
              {activeTab === 'staging' && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          if (selectedRecords.length === 0) {
                            toast.error('Please select records to convert');
                            return;
                          }
                          handleBulkConvert();
                        }}
                        disabled={selectedRecords.length === 0}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors duration-300 ${
                          selectedRecords.length === 0
                            ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                            : (isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-600 text-white hover:bg-green-700')
                        }`}
                      >
                        Convert to Payment ({selectedRecords.length})
                      </button>
                      <button
                        onClick={() => setShowBulkRejectModal(true)}
                        disabled={selectedRecords.length === 0}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors duration-300 ${
                          selectedRecords.length === 0
                            ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                            : (isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700')
                        }`}
                      >
                        Bulk Reject ({selectedRecords.length})
                      </button>
                      {selectedRecords.length > 0 && (
                        <button
                          onClick={() => setSelectedRecords([])}
                          className={`px-3 py-1 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {stagingRecords.length === 0 ? (
                    <p className={`text-center py-4 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>No staging records</p>
                  ) : (
                    <div className="space-y-4">
                      {stagingRecords.map((record) => (
                        <div key={record._id} className={`border rounded-lg p-4 transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-card-dark' : 'border-gray-200 bg-white'
                        }`}>
                          <div className="flex items-start space-x-4">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(record._id)}
                              onChange={() => toggleRecordSelection(record._id)}
                              className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark' : 'border-gray-300 bg-white'
                              }`}
                            />
                            <div className="flex-1">
                              <h3 className={`font-medium transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-100' : 'text-gray-900'
                              }`}>
                                {record.rawEmail?.subject || 'No subject'}
                              </h3>
                              <p className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                From: {record.rawEmail?.from?.name} ({record.rawEmail?.from?.email})
                              </p>
                              <div className="mt-2 flex items-center space-x-4 flex-wrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                                  record.qualityMetrics.overallConfidence >= 0.8 
                                    ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                                    : record.qualityMetrics.overallConfidence >= 0.6
                                    ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800')
                                    : (isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800')
                                }`}>
                                  {Math.round(record.qualityMetrics.overallConfidence * 100)}% confidence
                                </span>
                                <span className={`text-sm transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  Status: {record.stagingStatus}
                                </span>
                                {record.extractedData?.customer?.name?.value && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Customer: {record.extractedData.customer.name.value}
                                  </span>
                                )}
                                {record.extractedData?.passengers?.totalCount?.value && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Passengers: {record.extractedData.passengers.totalCount.value}
                                  </span>
                                )}
                                {record.extractedData?.serviceRequirements?.additionalServices?.bags?.count && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Bags: {record.extractedData.serviceRequirements.additionalServices.bags.count}
                                  </span>
                                )}
                                {record.extractedData?.flightDetails?.flightNumber?.value && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Flight: {record.extractedData.flightDetails.flightNumber.value}
                                  </span>
                                )}
                                {record.extractedData?.serviceRequirements?.serviceDate?.value && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Date: {record.extractedData.serviceRequirements.serviceDate.value}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleReviewRecord(record)}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                              >
                                Review
                              </button>
                              <button
                                onClick={() => handleConvertToPayment(record._id)}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                              >
                                Convert
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`px-3 py-1 text-sm rounded transition-colors duration-300 ${
                            currentPage === 1
                              ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                              : (isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700')
                          }`}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`px-3 py-1 text-sm rounded transition-colors duration-300 ${
                            currentPage === totalPages
                              ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                              : (isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700')
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Promoted Tab */}
              {activeTab === 'promoted' && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          if (selectedRecords.length === 0) {
                            toast.error('Please select records to delete');
                            return;
                          }
                          handleBulkDelete('promoted');
                        }}
                        disabled={selectedRecords.length === 0}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors duration-300 ${
                          selectedRecords.length === 0
                            ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                            : (isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700')
                        }`}
                      >
                        Delete Selected ({selectedRecords.length})
                      </button>
                      {promotedRecords.length > 0 && (
                        <button
                          onClick={() => {
                            const allIds = promotedRecords.map(record => record._id);
                            setSelectedRecords(allIds);
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Select All ({promotedRecords.length})
                        </button>
                      )}
                      {selectedRecords.length > 0 && (
                        <button
                          onClick={() => setSelectedRecords([])}
                          className={`px-3 py-1 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {promotedRecords.length === 0 ? (
                    <p className={`text-center py-4 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>No promoted records</p>
                  ) : (
                    <div className="space-y-4">
                      {promotedRecords.map((record) => (
                        <div key={record._id} className={`border rounded-lg p-4 transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-card-dark' : 'border-gray-200 bg-white'
                        }`}>
                          <div className="flex items-start space-x-4">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(record._id)}
                              onChange={() => toggleRecordSelection(record._id)}
                              className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark' : 'border-gray-300 bg-white'
                              }`}
                            />
                            <div className="flex-1">
                              <h3 className={`font-medium transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-100' : 'text-gray-900'
                              }`}>
                                {record.rawEmail?.subject || 'No subject'}
                              </h3>
                              <p className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                From: {record.rawEmail?.from?.name} ({record.rawEmail?.from?.email})
                              </p>
                              <div className="mt-2 flex items-center space-x-4 flex-wrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                                  isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800'
                                }`}>
                                  ✅ Promoted
                                </span>
                                <span className={`text-sm transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  Promoted: {new Date(record.promotedAt).toLocaleDateString()}
                                </span>
                                {record.extractedData?.customer?.name?.value && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Customer: {record.extractedData.customer.name.value}
                                  </span>
                                )}
                                {record.promotedToPaymentRequestId && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Payment ID: {record.promotedToPaymentRequestId}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleReviewRecord(record)}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Rejected Tab */}
              {activeTab === 'rejected' && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          if (selectedRecords.length === 0) {
                            toast.error('Please select records to delete');
                            return;
                          }
                          handleBulkDelete('rejected');
                        }}
                        disabled={selectedRecords.length === 0}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors duration-300 ${
                          selectedRecords.length === 0
                            ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                            : (isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700')
                        }`}
                      >
                        Delete Selected ({selectedRecords.length})
                      </button>
                      {rejectedRecords.length > 0 && (
                        <button
                          onClick={() => {
                            const allIds = rejectedRecords.map(record => record._id);
                            setSelectedRecords(allIds);
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Select All ({rejectedRecords.length})
                        </button>
                      )}
                      {selectedRecords.length > 0 && (
                        <button
                          onClick={() => setSelectedRecords([])}
                          className={`px-3 py-1 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {rejectedRecords.length === 0 ? (
                    <p className={`text-center py-4 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>No rejected records</p>
                  ) : (
                    <div className="space-y-4">
                      {rejectedRecords.map((record) => (
                        <div key={record._id} className={`border rounded-lg p-4 transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-card-dark' : 'border-gray-200 bg-white'
                        }`}>
                          <div className="flex items-start space-x-4">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(record._id)}
                              onChange={() => toggleRecordSelection(record._id)}
                              className={`mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded transition-colors duration-300 ${
                                isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark' : 'border-gray-300 bg-white'
                              }`}
                            />
                            <div className="flex-1">
                              <h3 className={`font-medium transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-100' : 'text-gray-900'
                              }`}>
                                {record.rawEmail?.subject || 'No subject'}
                              </h3>
                              <p className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                From: {record.rawEmail?.from?.name} ({record.rawEmail?.from?.email})
                              </p>
                              <div className="mt-2 flex items-center space-x-4 flex-wrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                                  isDarkMode ? 'bg-red-900/40 text-red-300 border border-red-700' : 'bg-red-100 text-red-800'
                                }`}>
                                  ❌ Rejected
                                </span>
                                <span className={`text-sm transition-colors duration-300 ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  Rejected: {new Date(record.approvedAt).toLocaleDateString()}
                                </span>
                                {record.rejectionReason && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Reason: {record.rejectionReason}
                                  </span>
                                )}
                                {record.extractedData?.customer?.name?.value && (
                                  <span className={`text-sm transition-colors duration-300 ${
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    Customer: {record.extractedData.customer.name.value}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleReviewRecord(record)}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>


      {/* Review Modal */}
      {showReviewModal && selectedRecord && (
        <ReviewModal
          record={selectedRecord}
          onClose={() => setShowReviewModal(false)}
          onReject={handleRejectRecord}
          onConvert={handleConvertToPayment}
          isConverting={convertingRecord === selectedRecord?._id}
        />
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
          }`}>
            <div className="mt-3">
              <h3 className={`text-lg font-medium mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Bulk Reject Staging Records
              </h3>
              <p className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                You are about to reject {selectedRecords.length} staging record(s). Please provide a reason for rejection.
              </p>
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Rejection Reason *
                </label>
                <textarea
                  value={bulkRejectReason}
                  onChange={(e) => setBulkRejectReason(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-300 ${
                    isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  placeholder="Enter reason for rejection..."
                  rows="3"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowBulkRejectModal(false);
                    setBulkRejectReason('');
                  }}
                  className={`px-4 py-2 rounded-md transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark text-zinc-300 hover:bg-skyone-border-dark' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkReject}
                  disabled={loading || !bulkRejectReason.trim()}
                  className={`px-4 py-2 rounded-md transition-colors duration-300 ${
                    loading || !bulkRejectReason.trim()
                      ? (isDarkMode ? 'bg-skyone-inset-dark text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                      : (isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700')
                  }`}
                >
                  {loading ? 'Rejecting...' : 'Reject Records'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple Review Modal Component
const ReviewModal = ({ record, onClose, onReject, onConvert, isConverting }) => {
  const { isDarkMode } = useAdminTheme();
  const [modifications, setModifications] = useState({});
  const [rejectionReason, setRejectionReason] = useState('');


  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    onReject(record._id, rejectionReason);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
      }`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>Review Email Processing</h2>
            <button
              onClick={onClose}
              className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original Email */}
            <div>
              <h3 className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>Original Email</h3>
              <div className={`p-4 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <p className={`font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{record.rawEmail?.subject}</p>
                <p className={`text-sm mt-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  From: {record.rawEmail?.from?.name} ({record.rawEmail?.from?.email})
                </p>
                <p className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Date: {new Date(record.rawEmail?.receivedAt).toLocaleString()}
                </p>
                <div className="mt-3 text-sm">
                  <p className={`font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Full Email Content:</p>
                  <div className={`p-3 rounded border max-h-96 overflow-y-auto transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
                  }`}>
                    <pre className={`whitespace-pre-wrap text-xs transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {record.rawEmail?.rawBody?.text || 
                       record.rawEmail?.conversationChain?.[0]?.body || 
                       record.rawEmail?.rawBody?.html?.replace(/<[^>]*>/g, '') || 
                       'No content available'}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Extracted Data */}
            <div>
              <h3 className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>Extracted Data</h3>
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Customer Name</label>
                  <input
                    type="text"
                    value={modifications.customer?.name?.value || record.extractedData.customer?.name?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      customer: {
                        ...modifications.customer,
                        name: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Email</label>
                  <input
                    type="email"
                    value={modifications.customer?.email?.value || record.extractedData.customer?.email?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      customer: {
                        ...modifications.customer,
                        email: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Service Type</label>
                  <select
                    value={modifications.serviceRequirements?.serviceType?.value || record.extractedData.serviceRequirements?.serviceType?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      serviceRequirements: {
                        ...modifications.serviceRequirements,
                        serviceType: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="ARRIVAL">Arrival</option>
                    <option value="DEPARTURE">Departure</option>
                    <option value="TRANSIT">Transit</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Service Level</label>
                  <select
                    value={modifications.serviceRequirements?.categoryLevel?.value || record.extractedData.serviceRequirements?.categoryLevel?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      serviceRequirements: {
                        ...modifications.serviceRequirements,
                        categoryLevel: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    <option value="Bronze">Bronze</option>
                    <option value="Silver">Silver</option>
                    <option value="Gold">Gold</option>
                    <option value="VIP">VIP</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Number of Passengers</label>
                  <input
                    type="number"
                    min="1"
                    value={modifications.passengers?.totalCount?.value || record.extractedData.passengers?.totalCount?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      passengers: {
                        ...modifications.passengers,
                        totalCount: { value: parseInt(e.target.value) || 1, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Number of Bags</label>
                  <input
                    type="number"
                    min="0"
                    value={modifications.serviceRequirements?.additionalServices?.bags?.count || record.extractedData.serviceRequirements?.additionalServices?.bags?.count || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      serviceRequirements: {
                        ...modifications.serviceRequirements,
                        additionalServices: {
                          ...modifications.serviceRequirements?.additionalServices,
                          bags: { 
                            count: parseInt(e.target.value) || 0, 
                            confidence: 1.0,
                            mentioned: true
                          }
                        }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Service Date</label>
                  <input
                    type="date"
                    value={modifications.serviceRequirements?.serviceDate?.value || record.extractedData.serviceRequirements?.serviceDate?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      serviceRequirements: {
                        ...modifications.serviceRequirements,
                        serviceDate: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Service Time</label>
                  <input
                    type="time"
                    value={modifications.serviceRequirements?.serviceTime?.value || record.extractedData.serviceRequirements?.serviceTime?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      serviceRequirements: {
                        ...modifications.serviceRequirements,
                        serviceTime: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Flight Number</label>
                  <input
                    type="text"
                    value={modifications.flightDetails?.flightNumber?.value || record.extractedData.flightDetails?.flightNumber?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      flightDetails: {
                        ...modifications.flightDetails,
                        flightNumber: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Departure Airport</label>
                  <input
                    type="text"
                    value={modifications.flightDetails?.from?.value || record.extractedData.flightDetails?.from?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      flightDetails: {
                        ...modifications.flightDetails,
                        from: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Arrival Airport</label>
                  <input
                    type="text"
                    value={modifications.flightDetails?.to?.value || record.extractedData.flightDetails?.to?.value || ''}
                    onChange={(e) => setModifications({
                      ...modifications,
                      flightDetails: {
                        ...modifications.flightDetails,
                        to: { value: e.target.value, confidence: 1.0 }
                      }
                    })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-md transition-colors duration-300 ${
                isDarkMode ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => onConvert(record._id, modifications)}
              disabled={isConverting}
              className={`px-4 py-2 rounded-md transition-colors flex items-center space-x-2 ${
                isConverting 
                  ? (isDarkMode ? 'bg-blue-500 text-white cursor-not-allowed' : 'bg-blue-400 text-white cursor-not-allowed')
                  : (isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700')
              }`}
            >
              {isConverting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Converting...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <span>Convert to Payment Request</span>
                </>
              )}
            </button>
          </div>

          {/* Rejection Reason Input */}
          <div className="mt-4">
            <label className={`block text-sm font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>Rejection Reason</label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
              }`}
              rows="2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedEmailDashboard;
